import type { TrackHoursTermScope } from './trackHoursTermScopeStorage';

export type HoursAdjustTrackPlanInput = {
  trackName: string;
  termScope: TrackHoursTermScope;
  excludeFromAdjust: boolean;
  criteriaHours: number;
  /** 학기별 실제 수업운영 시수(통합 표시와 무관하게 학기 분리 값) */
  operatingTerm1: number;
  operatingTerm2: number;
  /** 학기별 고정 프로그램 시수(편성 = 실수업 + 고정) */
  fixedTerm1: number;
  fixedTerm2: number;
  /** 구분 편제: 학기별 학습내용 시수 */
  learningTerm1: number | null;
  learningTerm2: number | null;
  /** 통합 편제: 연간 학습내용 시수 */
  learningAnnual: number | null;
};

export type HoursAdjustPhase1Row = {
  trackName: string;
  termScope: TrackHoursTermScope;
  criteriaHours: number;
  /** 조정 전·후 실 수업 시수 */
  currentTerm1: number;
  currentTerm2: number;
  currentAnnual: number;
  adjustedTerm1: number;
  adjustedTerm2: number;
  adjustedAnnual: number;
  /** 조정 전·후 편성 시수(실수업 + 고정P) */
  currentScheduledTerm1: number;
  currentScheduledTerm2: number;
  currentScheduledAnnual: number;
  adjustedScheduledTerm1: number;
  adjustedScheduledTerm2: number;
  adjustedScheduledAnnual: number;
  /** 조정 총 편성 시수 − 기준시수 (≥0 목표) */
  criteriaDelta: number;
  deltaTerm1: number;
  deltaTerm2: number;
};

export type HoursAdjustPhase2Row = {
  trackName: string;
  termScope: TrackHoursTermScope;
  criteriaHours: number;
  /** 1차 결과 실 수업 시수 */
  phase1Term1: number;
  phase1Term2: number;
  phase1Annual: number;
  /** 2차 조정 실 수업 시수 */
  adjustedTerm1: number;
  adjustedTerm2: number;
  adjustedAnnual: number;
  /** 2차 편성 시수(실수업 + 고정P) */
  adjustedScheduledTerm1: number;
  adjustedScheduledTerm2: number;
  adjustedScheduledAnnual: number;
  /** 조정 총 편성 시수 − 기준시수 */
  criteriaDelta: number;
  /** 2차 − 1차 (실수업) */
  deltaTerm1: number;
  deltaTerm2: number;
  /** 시간표 − 학습계획 (학기별; 통합은 연간을 term1에만) */
  learningDeltaTerm1: number | null;
  learningDeltaTerm2: number | null;
  note: string;
};

export type HoursAdjustPhase2Result = {
  rows: HoursAdjustPhase2Row[];
  totalOperatingTerm1: number;
  totalOperatingTerm2: number;
  /** 성립한 1시간 맞교환 횟수 */
  swapCount: number;
};

/** 1차 조정 학기 범위 */
export type HoursAdjustPhase1Mode = 'both_terms' | 'term2_only';

export type HoursAdjustPhase1Result = {
  /** both_terms: 1·2학기 재배분 / term2_only: 1학기 고정·2학기만 조정(2차 조정 불가) */
  mode: HoursAdjustPhase1Mode;
  ratioTerm1: number;
  ratioTerm2: number;
  totalOperatingTerm1: number;
  totalOperatingTerm2: number;
  /** 모든 편제가 기준시수를 맞추려면 추가로 필요한 수업시수(0이면 충족 가능) */
  shortfallHours: number;
  /** shortfallHours > 0 또는 1학기 고정으로 이전 불가분이 있을 때 안내 */
  shortfallMessage: string | null;
  rows: HoursAdjustPhase1Row[];
};

function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * weights 비율로 total을 정수 배분. 각 항목은 0..caps[i] 범위.
 * weights 합이 0이면 균등(캡 있는 항목 우선).
 */
function allocateByWeights(weights: number[], total: number, caps: number[]): number[] {
  const n = weights.length;
  const out = Array.from({ length: n }, () => 0);
  if (n === 0 || total <= 0) return out;

  const safeCaps = caps.map((c) => Math.max(0, Math.floor(c)));
  const safeWeights = weights.map((w, i) => (safeCaps[i]! > 0 ? Math.max(0, w) : 0));
  const weightSum = safeWeights.reduce((s, w) => s + w, 0);
  const maxTotal = safeCaps.reduce((s, c) => s + c, 0);
  let remain = Math.min(total, maxTotal);
  if (remain <= 0) return out;

  if (weightSum <= 0) {
    // 균등 배분
    while (remain > 0) {
      let progressed = false;
      for (let i = 0; i < n; i += 1) {
        if (remain <= 0) break;
        if (out[i]! < safeCaps[i]!) {
          out[i]! += 1;
          remain -= 1;
          progressed = true;
        }
      }
      if (!progressed) break;
    }
    return out;
  }

  const parts = safeWeights.map((w, i) => {
    const exact = (w / weightSum) * remain;
    const floor = Math.min(safeCaps[i]!, Math.floor(exact));
    return { i, floor, frac: exact - Math.floor(exact), cap: safeCaps[i]! };
  });
  parts.forEach((p) => {
    out[p.i] = p.floor;
  });
  let assigned = out.reduce((s, v) => s + v, 0);
  remain -= assigned;

  const order = [...parts].sort((a, b) => b.frac - a.frac || a.i - b.i);
  let guard = 0;
  while (remain > 0 && guard < n * 4) {
    guard += 1;
    let progressed = false;
    for (const p of order) {
      if (remain <= 0) break;
      if (out[p.i]! < p.cap) {
        out[p.i]! += 1;
        remain -= 1;
        progressed = true;
      }
    }
    if (!progressed) break;
  }
  return out;
}

/**
 * 1차: 여유 편제 → 부족 편제로 시수를 옮겨 기준 편차(조정 총 편성−기준)를 최대한 0 이상으로 맞춘다.
 * 비교·이전 단위는 편성 시수(실수업 + 고정P). 연간 총 편성 시수는 늘리지 않는다.
 * 고정P는 이동 불가이므로 기부는 실수업 여유분까지만 한다.
 * 학기 배분(기본): 조정 전 1·2학기 편성/실수업 총합을 그대로 유지한다.
 * freezeTerm1: 편제별 1학기 실수업을 고정하고, 2학기 실수업만으로 여유→부족을 맞춘다.
 */
export function computeHoursAdjustPhase1(
  inputs: HoursAdjustTrackPlanInput[],
  options?: { freezeTerm1?: boolean }
): HoursAdjustPhase1Result {
  const freezeTerm1 = Boolean(options?.freezeTerm1);
  const mode: HoursAdjustPhase1Mode = freezeTerm1 ? 'term2_only' : 'both_terms';
  const all = inputs.filter((t) => !t.excludeFromAdjust);
  const items = all.map((t) => {
    const currentTerm1 = Math.max(0, Math.round(t.operatingTerm1));
    const currentTerm2 = Math.max(0, Math.round(t.operatingTerm2));
    const fixedTerm1 = Math.max(0, Math.round(t.fixedTerm1));
    const fixedTerm2 = Math.max(0, Math.round(t.fixedTerm2));
    const currentAnnual = currentTerm1 + currentTerm2;
    const fixedAnnual = fixedTerm1 + fixedTerm2;
    const scheduledTerm1 = currentTerm1 + fixedTerm1;
    const scheduledTerm2 = currentTerm2 + fixedTerm2;
    const scheduledAnnual = scheduledTerm1 + scheduledTerm2;
    const criteriaHours = Math.max(0, Math.round(t.criteriaHours));
    const surplus = Math.max(0, scheduledAnnual - criteriaHours);
    const deficit = Math.max(0, criteriaHours - scheduledAnnual);
    /** 고정P는 옮길 수 없음. 1학기 고정 시에는 2학기 실수업만 기부 가능 */
    const movableCap = freezeTerm1 ? currentTerm2 : currentAnnual;
    return {
      track: t,
      currentTerm1,
      currentTerm2,
      currentAnnual,
      fixedTerm1,
      fixedTerm2,
      fixedAnnual,
      scheduledTerm1,
      scheduledTerm2,
      scheduledAnnual,
      criteriaHours,
      surplus,
      deficit,
      movableSurplus: Math.min(surplus, movableCap),
    };
  });

  const totalScheduledTerm1 = items.reduce((s, t) => s + t.scheduledTerm1, 0);
  const totalScheduledTerm2 = items.reduce((s, t) => s + t.scheduledTerm2, 0);
  const totalScheduled = totalScheduledTerm1 + totalScheduledTerm2;
  const ratioTerm1 = totalScheduled > 0 ? totalScheduledTerm1 / totalScheduled : 0.5;
  const ratioTerm2 = 1 - ratioTerm1;

  /** 학기 총합 고정 기준: 조정 전 실수업 1학기 합(고정P는 학기별 고정) */
  const preservedOperatingTerm1 = items.reduce((s, t) => s + t.currentTerm1, 0);

  const totalSurplus = items.reduce((s, t) => s + t.surplus, 0);
  const totalDeficit = items.reduce((s, t) => s + t.deficit, 0);
  const totalMovableSurplus = items.reduce((s, t) => s + t.movableSurplus, 0);
  const transfer = Math.min(totalMovableSurplus, totalDeficit);
  /** 편성 시수 총합이 기준 합에 미달하는 분 */
  const shortfallHours = Math.max(0, totalDeficit - totalSurplus);
  /** 이전 가능분 부족으로 채우지 못한 기준 부족분(1학기 고정·고정P 잠금 등) */
  const unmetByTransfer = Math.max(0, totalDeficit - transfer);

  const targetScheduledAnnual = items.map((t) => t.scheduledAnnual);
  if (transfer > 0) {
    const donate = allocateByWeights(
      items.map((t) => t.movableSurplus),
      transfer,
      items.map((t) => t.movableSurplus)
    );
    const receive = allocateByWeights(
      items.map((t) => t.deficit),
      transfer,
      items.map((t) => t.deficit)
    );
    items.forEach((_, i) => {
      targetScheduledAnnual[i]! = targetScheduledAnnual[i]! - donate[i]! + receive[i]!;
    });
  }

  // 편성 총합 유지 → 실수업 목표 = 편성 목표 − 고정P
  const targetOperatingAnnual = items.map((t, i) =>
    Math.max(0, targetScheduledAnnual[i]! - t.fixedAnnual)
  );

  /**
   * 학기 분할
   * - freezeTerm1: 편제별 1학기 실수업 = 조정 전 값 고정 → 변화는 전부 2학기
   * - 기본: 편제별 연간 목표는 지키되, 학년 전체 1학기 실수업 합 = 조정 전 값 고정.
   *   (고정P는 학기별로 그대로이므로 편성 시수 1·2학기 총합도 함께 보존된다)
   */
  let term1Alloc: number[];
  if (freezeTerm1) {
    term1Alloc = items.map((t, i) => Math.min(t.currentTerm1, targetOperatingAnnual[i]!));
  } else {
    const term1PreferWeights = items.map((t, i) => {
      const annual = targetOperatingAnnual[i]!;
      if (annual <= 0) return 0;
      if (t.currentAnnual <= 0) return annual * ratioTerm1;
      return annual * (t.currentTerm1 / t.currentAnnual);
    });
    term1Alloc = allocateByWeights(
      term1PreferWeights,
      preservedOperatingTerm1,
      targetOperatingAnnual
    );
  }

  const rows: HoursAdjustPhase1Row[] = items.map((t, index) => {
    const adjustedAnnual = targetOperatingAnnual[index]!;
    const adjustedTerm1 = term1Alloc[index]!;
    const adjustedTerm2 = adjustedAnnual - adjustedTerm1;
    const adjustedScheduledTerm1 = adjustedTerm1 + t.fixedTerm1;
    const adjustedScheduledTerm2 = adjustedTerm2 + t.fixedTerm2;
    const adjustedScheduledAnnual = adjustedScheduledTerm1 + adjustedScheduledTerm2;
    return {
      trackName: t.track.trackName,
      termScope: t.track.termScope,
      criteriaHours: t.criteriaHours,
      currentTerm1: t.currentTerm1,
      currentTerm2: t.currentTerm2,
      currentAnnual: t.currentAnnual,
      adjustedTerm1,
      adjustedTerm2,
      adjustedAnnual,
      currentScheduledTerm1: t.scheduledTerm1,
      currentScheduledTerm2: t.scheduledTerm2,
      currentScheduledAnnual: t.scheduledAnnual,
      adjustedScheduledTerm1,
      adjustedScheduledTerm2,
      adjustedScheduledAnnual,
      criteriaDelta: adjustedScheduledAnnual - t.criteriaHours,
      deltaTerm1: adjustedTerm1 - t.currentTerm1,
      deltaTerm2: adjustedTerm2 - t.currentTerm2,
    };
  });

  let shortfallMessage: string | null = null;
  if (shortfallHours > 0) {
    shortfallMessage = `총 편성 시수가 기준 시수 합을 넘지 않아 기준시수를 맞출 수 없습니다. 최소 기준을 맞추기 위해서는 편성을 +${shortfallHours}시간 더 해야 합니다.`;
  } else if (freezeTerm1 && unmetByTransfer > 0) {
    shortfallMessage = `1학기 시간표를 고정한 상태에서는 2학기 실수업 여유분만 옮길 수 있어, 기준시수를 ${unmetByTransfer}시간만큼 맞추지 못했습니다.`;
  }

  return {
    mode,
    ratioTerm1,
    ratioTerm2,
    totalOperatingTerm1: totalScheduledTerm1,
    totalOperatingTerm2: totalScheduledTerm2,
    shortfallHours,
    shortfallMessage,
    rows,
  };
}

/**
 * 2차 조정 규칙 요약
 * ────────────────────────────────────────────────────────────
 * 목표
 *  A) 구분 편제: 학습계획 편차(실수업 − 학습계획)를 학기별로 최대한 줄인다.
 *  B) 통합 편제: 학년 1·2학기 시수 비율에 가깝게 학기를 고르게 맞춘다.
 *     (학습 완충으로 한쪽에 몰린 뒤, 통합↔통합 맞교환으로 재분산)
 *
 * 불변 조건
 *  1) 편제별 연간 실수업 시수 = 1차 결과와 동일
 *  2) 학년 전체 1학기·2학기 실수업 합 = 1차와 동일
 *     → 한 편제의 1→2학기 이동은 반드시 다른 편제의 2→1학기 맞교환
 *  3) 고정 프로그램 시수는 이동하지 않음(실수업만 교환)
 *
 * 구분 편제 (학기별 학습계획 고정)
 *  - 연간 여유/부족 S = (연간실수업 − 연간학습) 은 학기 이동으로 변하지 않음
 *  - 이상: S≥0 이면 +편차를 1·2학기에 균등(+4/+4), S<0 이면 −편차를 균등 분배
 *
 * 통합 편제 학기 비율
 *  - 이상 t1 ≈ 연간 × (학년 1학기 비율). 예: 도덕 34시간·비율 52% → ≈18/16
 *  - 학습 맞교환으로 25/9처럼 치우치면, 다른 통합 편제(2학기 여유)와
 *    맞교환해 비율에 가깝게 되돌린다. 연간 학습편차는 변하지 않음.
 *
 * 탐색
 *  1패스: 학습편차 점수(+약한 학기비율) 개선 맞교환
 *  2패스: 학습편차를 악화시키지 않는 범위에서 학기비율 재균형(통합 우선)
 */

type Phase2WorkTrack = {
  trackName: string;
  termScope: TrackHoursTermScope;
  criteriaHours: number;
  fixedTerm1: number;
  fixedTerm2: number;
  annual: number;
  phase1Term1: number;
  /** 현재 탐색 중인 1학기 실수업 */
  t1: number;
  learningTerm1: number | null;
  learningTerm2: number | null;
  learningAnnual: number | null;
  /** 구분+학습입력 있음 → 편차 최소화 대상 */
  hasSplitLearning: boolean;
};

function evenSplitSurplus(surplus: number): { d1: number; d2: number } {
  const s = Math.round(surplus);
  const d1 = Math.floor(s / 2);
  return { d1, d2: s - d1 };
}

/** 구분 편제의 이상적 1학기 실수업 (연간·학습 고정 시) */
function idealTerm1ForSplit(annual: number, learningTerm1: number, learningTerm2: number): number {
  const A = Math.max(0, annual);
  const L1 = Math.max(0, learningTerm1);
  const L2 = Math.max(0, learningTerm2);
  const { d1 } = evenSplitSurplus(A - (L1 + L2));
  return clampInt(L1 + d1, 0, A);
}

/** 학년 1학기 비율에 맞춘 이상적 1학기 실수업 */
function idealTerm1ForRatio(annual: number, ratioTerm1: number): number {
  const A = Math.max(0, annual);
  if (A <= 0) return 0;
  return clampInt(A * ratioTerm1, 0, A);
}

/** 구분 편제 학습계획 편차 점수(낮을수록 좋음) */
function scorePhase2Learning(tracks: Phase2WorkTrack[]): number {
  let score = 0;
  for (const t of tracks) {
    if (!t.hasSplitLearning || t.learningTerm1 == null || t.learningTerm2 == null) continue;
    const t2 = t.annual - t.t1;
    const d1 = t.t1 - t.learningTerm1;
    const d2 = t2 - t.learningTerm2;
    const annualSurplus = d1 + d2;
    score += Math.abs(d1) + Math.abs(d2);
    score += 0.25 * Math.abs(d1 - d2);
    if (annualSurplus >= 0) {
      if (d1 < 0) score += 0.5 * -d1;
      if (d2 < 0) score += 0.5 * -d2;
    }
    const ideal = idealTerm1ForSplit(t.annual, t.learningTerm1, t.learningTerm2);
    score += 0.01 * Math.abs(t.t1 - ideal);
  }
  return score;
}

/**
 * 학기 비율 치우침 점수(낮을수록 좋음).
 * 통합 편제는 제곱 페널티로 도덕 25/9 같은 극단을 여러 편제에 나눠 담게 한다.
 * (±1 이내는 관용 — 학년 비율에 "가까우면" 충분)
 */
function scorePhase2TermRatio(tracks: Phase2WorkTrack[], ratioTerm1: number): number {
  let score = 0;
  for (const t of tracks) {
    const ideal = idealTerm1ForRatio(t.annual, ratioTerm1);
    const drift = Math.abs(t.t1 - ideal);
    if (t.termScope === 'unified') {
      const soft = Math.max(0, drift - 1);
      score += soft * soft;
    } else if (t.hasSplitLearning) {
      // 구분+학습: 학습 이상이 우선이므로 비율은 약하게만
      score += 0.05 * Math.max(0, drift - 2);
    } else {
      const soft = Math.max(0, drift - 1);
      score += 0.4 * soft * soft;
    }
  }
  return score;
}

/**
 * 1패스 종합 점수: 학습편차 ≫ 학기비율.
 * 학습을 우선하되, 통합이 완충재로 쓰일 때도 비율을 완전히 무시하지 않는다.
 */
function scorePhase2State(tracks: Phase2WorkTrack[], ratioTerm1: number): number {
  return scorePhase2Learning(tracks) * 12 + scorePhase2TermRatio(tracks, ratioTerm1);
}

/**
 * fromIdx: 1학기→2학기 1시간 이동(주는 쪽)
 * toIdx: 2학기→1학기 1시간 이동(받는 쪽)
 */
function canUnitSwap(tracks: Phase2WorkTrack[], fromIdx: number, toIdx: number): boolean {
  if (fromIdx === toIdx) return false;
  const from = tracks[fromIdx]!;
  const to = tracks[toIdx]!;
  if (from.t1 <= 0) return false;
  if (to.t1 >= to.annual) return false;
  // to가 2학기에서 1시간을 내줄 수 있어야 함
  if (to.annual - to.t1 <= 0) return false;
  return true;
}

function applyUnitSwap(tracks: Phase2WorkTrack[], fromIdx: number, toIdx: number): void {
  tracks[fromIdx]!.t1 -= 1;
  tracks[toIdx]!.t1 += 1;
}

type Phase2SwapMode = 'split-learning' | 'balanced' | 'ratio-only';

function phase2PassScore(
  mode: Phase2SwapMode,
  learning: number,
  ratio: number,
  ratioTerm1: number,
  tracks: Phase2WorkTrack[]
): number {
  if (mode === 'split-learning') return learning;
  if (mode === 'ratio-only') return ratio;
  return scorePhase2State(tracks, ratioTerm1);
}

function runPhase2SwapPass(params: {
  tracks: Phase2WorkTrack[];
  ratioTerm1: number;
  maxRounds: number;
  mode: Phase2SwapMode;
}): number {
  const { tracks, ratioTerm1, maxRounds, mode } = params;
  const n = tracks.length;
  let swapCount = 0;

  for (let round = 0; round < maxRounds; round += 1) {
    let bestGain = 0;
    let bestFrom = -1;
    let bestTo = -1;
    const baseLearning = scorePhase2Learning(tracks);
    const baseRatio = scorePhase2TermRatio(tracks, ratioTerm1);
    const baseScore = phase2PassScore(mode, baseLearning, baseRatio, ratioTerm1, tracks);

    for (let fromIdx = 0; fromIdx < n; fromIdx += 1) {
      for (let toIdx = 0; toIdx < n; toIdx += 1) {
        if (!canUnitSwap(tracks, fromIdx, toIdx)) continue;
        applyUnitSwap(tracks, fromIdx, toIdx);
        const nextLearning = scorePhase2Learning(tracks);
        const nextRatio = scorePhase2TermRatio(tracks, ratioTerm1);
        const nextScore = phase2PassScore(mode, nextLearning, nextRatio, ratioTerm1, tracks);
        // 롤백
        tracks[fromIdx]!.t1 += 1;
        tracks[toIdx]!.t1 -= 1;

        if (mode === 'ratio-only' && nextLearning > baseLearning + 1e-9) continue;
        if (mode === 'split-learning' && nextLearning > baseLearning + 1e-9) continue;

        const gain = baseScore - nextScore;
        if (gain > bestGain + 1e-9) {
          bestGain = gain;
          bestFrom = fromIdx;
          bestTo = toIdx;
        } else if (Math.abs(gain - bestGain) <= 1e-9 && gain > 1e-9) {
          const from = tracks[fromIdx]!;
          const to = tracks[toIdx]!;
          const curFrom = bestFrom >= 0 ? tracks[bestFrom]! : null;
          const curTo = bestTo >= 0 ? tracks[bestTo]! : null;
          const nextUnifiedPair =
            from.termScope === 'unified' && to.termScope === 'unified';
          const curUnifiedPair =
            curFrom?.termScope === 'unified' && curTo?.termScope === 'unified';
          const nextLearningTarget = from.hasSplitLearning || to.hasSplitLearning;
          const curLearningTarget =
            (curFrom?.hasSplitLearning ?? false) || (curTo?.hasSplitLearning ?? false);
          if (mode === 'ratio-only') {
            if (nextUnifiedPair && !curUnifiedPair) {
              bestFrom = fromIdx;
              bestTo = toIdx;
            }
          } else if (mode === 'split-learning') {
            if (nextLearningTarget && !curLearningTarget) {
              bestFrom = fromIdx;
              bestTo = toIdx;
            }
          } else if (
            (nextLearningTarget && !curLearningTarget) ||
            (nextLearningTarget === curLearningTarget && nextUnifiedPair && !curUnifiedPair)
          ) {
            bestFrom = fromIdx;
            bestTo = toIdx;
          }
        }
      }
    }

    if (bestFrom < 0 || bestTo < 0 || bestGain <= 1e-9) break;
    applyUnitSwap(tracks, bestFrom, bestTo);
    swapCount += 1;
  }

  return swapCount;
}

/**
 * 2차: 1차 학기 총량·편제 연간 시수를 유지한 채
 * (1) 학습계획 편차 최소화 → (2) 통합 편제 학기비율 재균형.
 */
export function computeHoursAdjustPhase2(params: {
  inputs: HoursAdjustTrackPlanInput[];
  phase1: HoursAdjustPhase1Result;
}): HoursAdjustPhase2Result {
  const { inputs, phase1 } = params;
  const inputByName = new Map(inputs.map((t) => [t.trackName, t]));
  const ratioTerm1 = phase1.ratioTerm1;

  const tracks: Phase2WorkTrack[] = phase1.rows.map((p1) => {
    const input = inputByName.get(p1.trackName);
    const termScope = input?.termScope ?? p1.termScope;
    const learningTerm1 = input?.learningTerm1 ?? null;
    const learningTerm2 = input?.learningTerm2 ?? null;
    const learningAnnual = input?.learningAnnual ?? null;
    const hasSplitLearning =
      termScope === 'split' && learningTerm1 != null && learningTerm2 != null;
    return {
      trackName: p1.trackName,
      termScope,
      criteriaHours: p1.criteriaHours,
      fixedTerm1: Math.max(0, p1.adjustedScheduledTerm1 - p1.adjustedTerm1),
      fixedTerm2: Math.max(0, p1.adjustedScheduledTerm2 - p1.adjustedTerm2),
      annual: p1.adjustedAnnual,
      phase1Term1: p1.adjustedTerm1,
      t1: p1.adjustedTerm1,
      learningTerm1,
      learningTerm2,
      learningAnnual,
      hasSplitLearning,
    };
  });

  const n = tracks.length;
  const maxRounds = Math.max(32, n * n * 8);

  // 1패스: 1·2학기 구분 편제의 학습계획 시수를 먼저 맞춤(통합은 완충만)
  let swapCount = runPhase2SwapPass({
    tracks,
    ratioTerm1,
    maxRounds,
    mode: 'split-learning',
  });

  // 2패스: 학습편차 + 약한 학기비율
  swapCount += runPhase2SwapPass({
    tracks,
    ratioTerm1,
    maxRounds,
    mode: 'balanced',
  });

  // 3패스: 학습편차 유지·학기비율 재균형(통합 편제 후순위)
  swapCount += runPhase2SwapPass({
    tracks,
    ratioTerm1,
    maxRounds,
    mode: 'ratio-only',
  });

  const rows: HoursAdjustPhase2Row[] = tracks.map((t) => {
    const adjustedTerm1 = t.t1;
    const adjustedTerm2 = t.annual - t.t1;
    const adjustedScheduledTerm1 = adjustedTerm1 + t.fixedTerm1;
    const adjustedScheduledTerm2 = adjustedTerm2 + t.fixedTerm2;
    const adjustedScheduledAnnual = adjustedScheduledTerm1 + adjustedScheduledTerm2;
    const idealRatioT1 = idealTerm1ForRatio(t.annual, ratioTerm1);
    let learningDeltaTerm1: number | null = null;
    let learningDeltaTerm2: number | null = null;
    let note = '변경 없음';

    if (t.hasSplitLearning && t.learningTerm1 != null && t.learningTerm2 != null) {
      learningDeltaTerm1 = adjustedTerm1 - t.learningTerm1;
      learningDeltaTerm2 = adjustedTerm2 - t.learningTerm2;
      const moved = adjustedTerm1 - t.phase1Term1;
      if (moved === 0) {
        note =
          Math.abs(learningDeltaTerm1) + Math.abs(learningDeltaTerm2) ===
          Math.abs(learningDeltaTerm1 + learningDeltaTerm2)
            ? '학기 편차 균형 유지'
            : '학습 편차 잔여(교환 상대 부족)';
      } else if (moved < 0) {
        note = `1→2학기 ${-moved}시간 맞교환`;
      } else {
        note = `2→1학기 ${moved}시간 맞교환`;
      }
    } else if (t.termScope === 'unified') {
      if (t.learningAnnual != null) {
        learningDeltaTerm1 = t.annual - t.learningAnnual;
        learningDeltaTerm2 = null;
      }
      const moved = adjustedTerm1 - t.phase1Term1;
      const ratioDrift = Math.abs(adjustedTerm1 - idealRatioT1);
      if (moved === 0 && ratioDrift <= 1) {
        note = '통합·학기비율 유지';
      } else if (moved === 0) {
        note = `통합·학기비율 잔여(이상 ${idealRatioT1}/${t.annual - idealRatioT1})`;
      } else if (moved < 0) {
        note = `통합 1→2학기 ${-moved}시간(비율 조정)`;
      } else {
        note = `통합 2→1학기 ${moved}시간(비율 조정)`;
      }
    } else {
      note = '학습계획 시수 미입력 — 교환 완충만 가능';
    }

    return {
      trackName: t.trackName,
      termScope: t.termScope,
      criteriaHours: t.criteriaHours,
      phase1Term1: t.phase1Term1,
      phase1Term2: t.annual - t.phase1Term1,
      phase1Annual: t.annual,
      adjustedTerm1,
      adjustedTerm2,
      adjustedAnnual: t.annual,
      adjustedScheduledTerm1,
      adjustedScheduledTerm2,
      adjustedScheduledAnnual,
      criteriaDelta: adjustedScheduledAnnual - t.criteriaHours,
      deltaTerm1: adjustedTerm1 - t.phase1Term1,
      deltaTerm2: adjustedTerm2 - (t.annual - t.phase1Term1),
      learningDeltaTerm1,
      learningDeltaTerm2,
      note,
    };
  });

  return {
    rows,
    totalOperatingTerm1: rows.reduce((s, r) => s + r.adjustedTerm1, 0),
    totalOperatingTerm2: rows.reduce((s, r) => s + r.adjustedTerm2, 0),
    swapCount,
  };
}

/** 사용자가 시간표에서 실행할 「A → B」 이전 항목 */
export type HoursAdjustChecklistTransfer = {
  id: string;
  term: 1 | 2;
  fromTrack: string;
  toTrack: string;
  hours: number;
};

export type HoursAdjustChecklist = {
  transfers: HoursAdjustChecklistTransfer[];
};

type ChecklistSourceTrack = {
  trackName: string;
  operatingTerm1: number;
  operatingTerm2: number;
};

type ChecklistDelta = {
  trackName: string;
  deltaTerm1: number;
  deltaTerm2: number;
};

/**
 * 원본 실수업 → 목표(1차 또는 2차) 증감을 학기별로 짝지어
 * 사용자가 옮기면 되는 A→B 항목만 만든다.
 * (이 목록을 모두 적용하면 편제별 시수가 목표 결과와 같아진다)
 */
export function buildHoursAdjustChecklist(params: {
  sources: ChecklistSourceTrack[];
  /** 1차·2차 모두 adjustedTerm1/2 만 있으면 됨 */
  target: {
    rows: Array<{ trackName: string; adjustedTerm1: number; adjustedTerm2: number }>;
  };
}): HoursAdjustChecklist {
  const { sources, target } = params;
  const sourceByName = new Map(sources.map((s) => [s.trackName, s]));

  const deltas: ChecklistDelta[] = [];
  for (const row of target.rows) {
    const src = sourceByName.get(row.trackName);
    if (!src) continue;
    const deltaTerm1 = row.adjustedTerm1 - Math.max(0, Math.round(src.operatingTerm1));
    const deltaTerm2 = row.adjustedTerm2 - Math.max(0, Math.round(src.operatingTerm2));
    if (deltaTerm1 === 0 && deltaTerm2 === 0) continue;
    deltas.push({ trackName: row.trackName, deltaTerm1, deltaTerm2 });
  }

  const pairTermTransfers = (term: 1 | 2): HoursAdjustChecklistTransfer[] => {
    const donors: Array<{ trackName: string; remain: number }> = [];
    const receivers: Array<{ trackName: string; remain: number }> = [];
    for (const c of deltas) {
      const d = term === 1 ? c.deltaTerm1 : c.deltaTerm2;
      if (d < 0) donors.push({ trackName: c.trackName, remain: -d });
      if (d > 0) receivers.push({ trackName: c.trackName, remain: d });
    }
    donors.sort((a, b) => b.remain - a.remain || a.trackName.localeCompare(b.trackName, 'ko'));
    receivers.sort((a, b) => b.remain - a.remain || a.trackName.localeCompare(b.trackName, 'ko'));

    const out: HoursAdjustChecklistTransfer[] = [];
    let di = 0;
    let ri = 0;
    let seq = 0;
    while (di < donors.length && ri < receivers.length) {
      const donor = donors[di]!;
      const receiver = receivers[ri]!;
      const hours = Math.min(donor.remain, receiver.remain);
      if (hours > 0) {
        seq += 1;
        out.push({
          id: `t${term}-${seq}-${donor.trackName}-${receiver.trackName}`,
          term,
          fromTrack: donor.trackName,
          toTrack: receiver.trackName,
          hours,
        });
        donor.remain -= hours;
        receiver.remain -= hours;
      }
      if (donor.remain <= 0) di += 1;
      if (receiver.remain <= 0) ri += 1;
    }
    return out;
  };

  return {
    transfers: [...pairTermTransfers(1), ...pairTermTransfers(2)],
  };
}

/**
 * 사용자 입력 1·2학기 편성 시수 맞춤.
 * 연간(기준시수) 재배분은 1차(both_terms)와 동일하고,
 * 학기 분할만 입력한 편성 시수(합=기준)를 우선한다.
 */
export function computeHoursAdjustUserTermTargets(
  inputs: HoursAdjustTrackPlanInput[],
  scheduledTargets: Record<string, { term1: number; term2: number }>,
): HoursAdjustPhase1Result {
  const base = computeHoursAdjustPhase1(inputs, { freezeTerm1: false });
  const inputByName = new Map(
    inputs.filter((t) => !t.excludeFromAdjust).map((t) => [t.trackName, t] as const),
  );

  const rows: HoursAdjustPhase1Row[] = base.rows.map((row) => {
    const inp = inputByName.get(row.trackName);
    if (!inp) return row;
    const fixed1 = Math.max(0, Math.round(inp.fixedTerm1));
    const fixed2 = Math.max(0, Math.round(inp.fixedTerm2));
    const criteria = Math.max(0, Math.round(row.criteriaHours));
    const rawT1 = scheduledTargets[row.trackName]?.term1;
    const preferredSched1 =
      rawT1 != null && Number.isFinite(rawT1)
        ? clampInt(rawT1, 0, criteria)
        : clampInt(row.adjustedScheduledTerm1, 0, criteria);
    const preferredOp1 = clampInt(preferredSched1 - fixed1, 0, row.adjustedAnnual);
    const adjustedTerm1 = preferredOp1;
    const adjustedTerm2 = row.adjustedAnnual - adjustedTerm1;
    const adjustedScheduledTerm1 = adjustedTerm1 + fixed1;
    const adjustedScheduledTerm2 = adjustedTerm2 + fixed2;
    return {
      ...row,
      adjustedTerm1,
      adjustedTerm2,
      adjustedScheduledTerm1,
      adjustedScheduledTerm2,
      adjustedScheduledAnnual: adjustedScheduledTerm1 + adjustedScheduledTerm2,
      criteriaDelta: adjustedScheduledTerm1 + adjustedScheduledTerm2 - criteria,
      deltaTerm1: adjustedTerm1 - row.currentTerm1,
      deltaTerm2: adjustedTerm2 - row.currentTerm2,
    };
  });

  return {
    ...base,
    mode: 'both_terms',
    totalOperatingTerm1: rows.reduce((s, r) => s + r.adjustedTerm1, 0),
    totalOperatingTerm2: rows.reduce((s, r) => s + r.adjustedTerm2, 0),
    rows,
  };
}

/** 1차 결과를 체크리스트 자동 반영 목표(Phase2 형태)로 변환 */
export function phase1ResultAsApplyTarget(phase1: HoursAdjustPhase1Result): HoursAdjustPhase2Result {
  return {
    rows: phase1.rows.map((r) => ({
      trackName: r.trackName,
      termScope: r.termScope,
      criteriaHours: r.criteriaHours,
      phase1Term1: r.currentTerm1,
      phase1Term2: r.currentTerm2,
      phase1Annual: r.currentAnnual,
      adjustedTerm1: r.adjustedTerm1,
      adjustedTerm2: r.adjustedTerm2,
      adjustedAnnual: r.adjustedAnnual,
      adjustedScheduledTerm1: r.adjustedScheduledTerm1,
      adjustedScheduledTerm2: r.adjustedScheduledTerm2,
      adjustedScheduledAnnual: r.adjustedScheduledAnnual,
      criteriaDelta: r.criteriaDelta,
      deltaTerm1: r.deltaTerm1,
      deltaTerm2: r.deltaTerm2,
      learningDeltaTerm1: null,
      learningDeltaTerm2: null,
      note: '',
    })),
    totalOperatingTerm1: phase1.totalOperatingTerm1,
    totalOperatingTerm2: phase1.totalOperatingTerm2,
    swapCount: 0,
  };
}
